import { z } from "zod";

export const HOUSE_IDS = [
  "sorting",
  "logic",
  "tiles",
  "arcade",
  "colors",
  "fill",
] as const;

export const houseIdSchema = z.enum(HOUSE_IDS);
export type HouseId = z.infer<typeof houseIdSchema>;

export const houseStatusSchema = z.enum([
  "available",
  "under_construction",
  "maintenance",
]);
export type HouseStatus = z.infer<typeof houseStatusSchema>;

export const houseManifestSchema = z.object({
  id: houseIdSchema,
  displayName: z.string().min(1),
  description: z.string().min(1),
  status: houseStatusSchema,
  levelCount: z.literal(100),
  contentVersion: z.string().min(1),
  buildingAssetId: z.string().min(1),
  entryPosition: z.object({
    x: z.number().finite(),
    y: z.number().finite(),
  }),
});
export type HouseManifest = z.infer<typeof houseManifestSchema>;

export const priceBandSchema = z
  .object({
    min: z.number().int().nonnegative(),
    max: z.number().int().nonnegative(),
  })
  .refine((band) => band.max >= band.min, {
    message: "price band max must be >= min",
  });

export const economySchema = z.object({
  firstClearGold: z.object({
    base: z.number().int().nonnegative(),
    perTenLevels: z.number().int().nonnegative(),
  }),
  firstThreeStarBonusGold: z.number().int().nonnegative(),
  /** Hints per level attempt: a few free, then each one costs gold (server-charged). */
  hints: z.object({
    freePerLevel: z.number().int().min(0).max(10),
    priceGold: z.number().int().min(1),
  }),
  cosmeticPriceGold: z.object({
    common: priceBandSchema,
    rare: priceBandSchema,
    epic: priceBandSchema,
  }),
  /**
   * Rewarded ads in the Google Play app, always opt-in. Gold is paid by the
   * Worker only after Google's signed reward callback; hints stay client-side.
   */
  ads: z.object({
    /** Gold in one city gift chest, and how many chests open per Vietnam day. */
    giftGold: z.number().int().min(1),
    giftsPerDay: z.number().int().min(0).max(10),
    /** Hints one ad adds to the current attempt. */
    hintsPerAd: z.number().int().min(1).max(3),
  }),
});
export type EconomyConfig = z.infer<typeof economySchema>;

export const featureFlagsSchema = z.object({
  leaderboardEnabled: z.boolean(),
  shopEnabled: z.boolean(),
  cloudSyncEnabled: z.boolean(),
});
export type FeatureFlags = z.infer<typeof featureFlagsSchema>;

/** Cloudflare Free budget knobs shared by client and Worker. */
export const networkBudgetSchema = z.object({
  /** Max level proofs per `/progress/sync` request (bounded by Worker CPU). */
  maxResultsPerSync: z.number().int().min(1).max(20),
  /** Client flushes the outbox once this many results are waiting. */
  syncFlushThreshold: z.number().int().min(1).max(20),
  /** Edge + client cache for the public top list. */
  leaderboardCacheSeconds: z.number().int().min(60).max(3600),
  leaderboardTopLimit: z.number().int().min(10).max(100),
  /** Me-rank stops counting past this many rows to protect D1 reads. */
  rankScanCap: z.number().int().min(100).max(20000),
});
export type NetworkBudget = z.infer<typeof networkBudgetSchema>;

const housesTupleSchema = z.tuple([
  houseManifestSchema,
  houseManifestSchema,
  houseManifestSchema,
  houseManifestSchema,
  houseManifestSchema,
  houseManifestSchema,
]);

export const gameConfigSchema = z
  .object({
    version: z.literal(1),
    displayName: z.string().min(1),
    credit: z.string().min(1),
    flags: featureFlagsSchema,
    economy: economySchema,
    network: networkBudgetSchema,
    houses: housesTupleSchema,
  })
  .superRefine((config, ctx) => {
    const ids = config.houses.map((house) => house.id);
    const missing = HOUSE_IDS.filter((id) => !ids.includes(id));
    const extras = ids.filter((id, index) => ids.indexOf(id) !== index);

    if (missing.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `missing houses: ${missing.join(", ")}`,
        path: ["houses"],
      });
    }

    if (extras.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `duplicate houses: ${extras.join(", ")}`,
        path: ["houses"],
      });
    }

    for (const [index, house] of config.houses.entries()) {
      if (house.id !== HOUSE_IDS[index]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `houses must be listed in city order; expected ${HOUSE_IDS[index]} at index ${index}`,
          path: ["houses", index, "id"],
        });
      }
    }
  });

export type GameConfig = z.infer<typeof gameConfigSchema>;

export const remoteHouseOverridesSchema = z
  .object({
    sorting: houseStatusSchema.optional(),
    logic: houseStatusSchema.optional(),
    tiles: houseStatusSchema.optional(),
    arcade: houseStatusSchema.optional(),
    colors: houseStatusSchema.optional(),
    fill: houseStatusSchema.optional(),
  })
  .strict();

export const remoteConfigOverrideSchema = z
  .object({
    houses: remoteHouseOverridesSchema.optional(),
    leaderboardEnabled: z.boolean().optional(),
    shopEnabled: z.boolean().optional(),
    cloudSyncEnabled: z.boolean().optional(),
  })
  .strict();
export type RemoteConfigOverride = z.infer<typeof remoteConfigOverrideSchema>;
